import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Checkbox, Drawer, Input, Skeleton, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import {
  BatchRefusedError,
  approveScenarioBatch,
  previewScenarioBatch,
  type BatchExclusion,
  type BatchItem,
  type ScenarioReviewQueue,
} from '@/api/scenarioVersions';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { shortHash } from '../scenarioWords';
import { RolePicker, cardStyle, surface, useDeclaredRole } from './reviewParts';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// BatchApprovalDrawer — screen 05, frame B. Stacked over the review drawer. The SERVER says which ticked versions a batch
// would leave out and why (Batch/Preview); this drawer lists the rest by version and hash, names every exclusion with
// the action that resolves it, and asks for an explicit confirmation of exactly those versions. The button names the
// real number. If anything moved between preview and confirm, the server records nothing and this re-checks.
// ─────────────────────────────────────────────────────────────────────────────

export const BatchApprovalDrawer = observer(function BatchApprovalDrawer({
  open,
  onClose,
  agentId,
  ticked,
  queue,
  viewerName,
  onOpenItem,
  onRecorded,
}: {
  open: boolean;
  onClose: () => void;
  agentId: number | null;
  ticked: BatchItem[];
  queue: ScenarioReviewQueue | null;
  viewerName?: string;
  onOpenItem: (scenarioId: string) => void;
  onRecorded: () => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [eligible, setEligible] = useState<BatchItem[] | null>(null);
  const [excluded, setExcluded] = useState<BatchExclusion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useDeclaredRole();
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [check, setCheck] = useState(0);
  const request = useRef(0);

  useEffect(() => {
    if (!open || agentId == null) return;
    const mine = ++request.current;
    setEligible(null);
    setError(null);
    setConfirmed(false);
    previewScenarioBatch(agentId, ticked)
      .then((p) => {
        if (mine !== request.current) return;
        setEligible(p.eligible);
        setExcluded(p.excluded);
      })
      .catch((e: Error) => mine === request.current && setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId, check]);

  const number = (id: string) => queue?.items.find((i) => i.scenarioId === id)?.current.number;
  const count = eligible?.length ?? 0;
  const card = cardStyle(isDark);
  const s = surface(isDark);

  const approve = async () => {
    if (agentId == null || !eligible) return;
    setSaving(true);
    setError(null);
    try {
      await approveScenarioBatch(agentId, { items: eligible, reviewerRole: role, justification: note.trim() });
      onRecorded();
    } catch (e) {
      setError(e instanceof BatchRefusedError && e.excluded.length > 0
        ? `${e.message} The list below is checked again.`
        : (e as Error).message);
      if (e instanceof BatchRefusedError) setCheck((n) => n + 1);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(640px, 100vw)"
      title={
        <div>
          <div><CheckOutlined /> Approve {count} of {ticked.length} selected</div>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>Policy: wording only · checks unchanged · batch allowed</Text>
        </div>
      }
      styles={{ body: { background: s.page, display: 'flex', flexDirection: 'column', gap: 12 } }}
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Recorded as {count} separate approvals, each naming your account, your declared role, the time and the version’s hash; none of
            them is clinical sign-off. The {excluded.length} left out stay open in the queue.
          </Text>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={saving} disabled={count === 0 || !confirmed || !role || !note.trim()} onClick={() => void approve()}>
              {saving ? 'Approving…' : `Approve ${count} ${count === 1 ? 'version' : 'versions'}`}
            </Button>
          </div>
        </div>
      }
    >
      {error && <Alert type="error" showIcon title="Nothing was recorded" description={error} />}
      {!eligible && !error && <Skeleton active paragraph={{ rows: 6 }} />}
      {eligible && (
        <>
          <section style={card} aria-label="Will be approved">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <StatusPill tone="violet">1</StatusPill>
              <Text strong>Will be approved</Text>
              <StatusPill tone="green">{count} {count === 1 ? 'version' : 'versions'}</StatusPill>
            </div>
            {count === 0 ? (
              <Text type="secondary">None of the selected versions can be approved together. Review each on its own.</Text>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: s.muted }}>
                    <th style={{ padding: '4px 0', fontWeight: 500 }}>Scenario</th>
                    <th style={{ fontWeight: 500 }}>Version</th>
                    <th style={{ fontWeight: 500 }}>Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {eligible.map((i) => (
                    <tr key={i.scenarioId} style={{ borderTop: `1px solid ${s.line}` }}>
                      <td style={{ padding: '6px 8px 6px 0', fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all' }}>{i.scenarioId}</td>
                      <td>v{number(i.scenarioId) ?? '?'}</td>
                      <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{shortHash(i.bodyHash)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {excluded.length > 0 && (
            <section style={card} aria-label="Left out">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <StatusPill tone="violet">2</StatusPill>
                <Text strong>Left out</Text>
                <StatusPill tone="amber">{excluded.length} {excluded.length === 1 ? 'version' : 'versions'}</StatusPill>
              </div>
              {excluded.map((x) => (
                <div key={`${x.scenarioId}-${x.snapshotId}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0', borderTop: `1px solid ${s.line}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, wordBreak: 'break-all' }}>{x.scenarioId}</div>
                    <Text type="secondary" style={{ fontSize: 12.5 }}>{x.reason}</Text>
                  </div>
                  <Button size="small" onClick={() => onOpenItem(x.scenarioId)}>{x.reason.startsWith('Changed after') ? 'What changed?' : 'Open'}</Button>
                </div>
              ))}
            </section>
          )}

          <section style={card} aria-label="Confirm">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <StatusPill tone="violet">{excluded.length > 0 ? 3 : 2}</StatusPill>
              <Text strong>Confirm</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12.5 }}>Recorded as <Text strong>{viewerName ?? 'you'}</Text> (signed in) · declared role</Text>
              <RolePicker value={role} onChange={setRole} />
            </div>
            <Input.TextArea rows={2} maxLength={4000} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What you checked (recorded on each approval)" aria-label="What you checked" />
            <Checkbox style={{ marginTop: 10 }} checked={confirmed} disabled={count === 0} onChange={(e) => setConfirmed(e.target.checked)}>
              I approve exactly these {count} versions, identified by the hashes above. Any later edit to one of them will need a new approval.
            </Checkbox>
          </section>
        </>
      )}
    </Drawer>
  );
});
